import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    TrendingUp,
    Users,
    CreditCard,
    Activity,
    ArrowUpRight,
    ArrowDownRight,
    Clock,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import api from '../../services/api';

const AdminDashboard = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await api.get('/dashboard/stats');
                if (res.data.success) {
                    setStats(res.data.data);
                }
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const recentActions = [
        { id: 1, type: 'System', message: 'Backend engine online and healthy', time: 'Just now', status: 'Success' },
        { id: 2, type: 'Security', message: 'Admin session initialized', time: '5 mins ago', status: 'Success' },
    ];

    if (loading) {
        return (
            <div className="h-[60vh] flex flex-col items-center justify-center text-slate-500">
                <Loader2 className="animate-spin text-primary mb-4" size={48} />
                <p className="font-bold uppercase text-[10px] tracking-[0.2em]">Synchronizing System Data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">System Overview</h1>
                    <p className="text-slate-400 mt-1 text-sm">Real-time performance and analytics dashboard</p>
                </div>
                <div className="flex gap-3">
                    <button className="px-5 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm font-medium hover:bg-slate-700 hover:text-white transition-all text-slate-300">Export Data</button>
                    <button className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-violet-600/20 active:scale-[0.98] transition-all flex items-center gap-2">
                        <Activity size={18} /> Generate Report
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.length > 0 ? stats.map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-slate-900 border border-slate-800 p-6 rounded-2xl hover:border-violet-500/30 transition-all group relative overflow-hidden shadow-sm"
                    >
                        <div className={`absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity`}>
                            {stat.icon === 'users' ? <Users size={100} /> : stat.icon === 'revenue' ? <CreditCard size={100} /> : stat.icon === 'active' ? <Activity size={100} /> : <TrendingUp size={100} />}
                        </div>

                        <div className="flex items-center justify-between mb-4 relative z-10">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.type === 'revenue' ? 'bg-emerald-500/10 text-emerald-500' :
                                stat.type === 'users' ? 'bg-blue-500/10 text-blue-500' :
                                    stat.type === 'active' ? 'bg-amber-500/10 text-amber-500' :
                                        'bg-violet-500/10 text-violet-500'
                                }`}>
                                {stat.type === 'users' ? <Users size={24} /> : stat.type === 'revenue' ? <CreditCard size={24} /> : stat.type === 'active' ? <Activity size={24} /> : <TrendingUp size={24} />}
                            </div>
                            <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg ${stat.trend === 'up' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'
                                }`}>
                                {stat.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                {stat.change}
                            </div>
                        </div>
                        <h3 className="text-slate-400 text-sm font-medium mb-1 relative z-10">{stat.label}</h3>
                        <p className="text-2xl font-bold text-white relative z-10">{stat.value}</p>
                    </motion.div>
                )) : (
                    // Fallback skeleton or empty state if stats fail to load or are empty
                    [1, 2, 3, 4].map((_, idx) => (
                        <div key={idx} className="bg-slate-900 border border-slate-800 p-6 rounded-2xl animate-pulse">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 bg-slate-800 rounded-xl"></div>
                                <div className="w-16 h-6 bg-slate-800 rounded-lg"></div>
                            </div>
                            <div className="w-24 h-4 bg-slate-800 rounded mb-2"></div>
                            <div className="w-16 h-8 bg-slate-800 rounded"></div>
                        </div>
                    ))
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Chart Section */}
                <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-3xl p-8 min-h-[450px] flex flex-col shadow-xl">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-xl font-bold text-white">Performance Analytics</h2>
                            <p className="text-slate-500 text-sm mt-1">Revenue and user growth over time</p>
                        </div>
                        <div className="flex bg-slate-800 rounded-xl p-1">
                            {['7D', '1M', '3M', '1Y'].map(t => (
                                <button key={t} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${t === '1M' ? 'bg-violet-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}>{t}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 flex items-center justify-center border-2 border-dashed border-slate-800 rounded-2xl bg-slate-950/30 group">
                        <div className="text-center">
                            <Activity className="mx-auto text-slate-700 mb-4 group-hover:text-violet-500 transition-colors" size={48} />
                            <p className="text-slate-500 font-medium text-sm">Interactive Chart Module</p>
                            <p className="text-slate-600 text-xs mt-1">Detailed analytics visualization coming soon</p>
                        </div>
                    </div>
                </div>

                {/* Recent Activity Feed */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-xl flex flex-col">
                    <h2 className="text-xl font-bold text-white mb-1">Recent Activity</h2>
                    <p className="text-slate-500 text-sm mb-6">Latest system events and logs</p>

                    <div className="space-y-6 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                        {[
                            { id: 1, type: 'System', message: 'Backend engine online', time: '2 mins ago', status: 'success', icon: Activity },
                            { id: 2, type: 'Security', message: 'Admin session verified', time: '15 mins ago', status: 'success', icon: CheckCircle2 },
                            { id: 3, type: 'User', message: 'New user registration', time: '1 hour ago', status: 'info', icon: Users },
                            { id: 4, type: 'Order', message: 'New order #1234 received', time: '2 hours ago', status: 'warning', icon: CreditCard },
                            { id: 5, type: 'System', message: 'Database backup completed', time: '5 hours ago', status: 'success', icon: Activity },
                        ].map((action, idx) => (
                            <div key={action.id} className="flex gap-4 group">
                                <div className="mt-1 relative">
                                    <div className="absolute inset-0 bg-slate-800 w-px h-full left-1/2 -ml-px -z-10" />
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-slate-900 shadow-sm ${action.status === 'success' ? 'bg-emerald-500 text-white' :
                                        action.status === 'warning' ? 'bg-amber-500 text-white' :
                                            'bg-blue-500 text-white'
                                        }`}>
                                        <action.icon size={14} />
                                    </div>
                                </div>
                                <div className="pb-4 border-b border-slate-800/50 w-full group-last:border-0">
                                    <p className="text-slate-300 font-medium text-sm group-hover:text-white transition-colors">{action.message}</p>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">{action.type}</span>
                                        <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                                        <span className="text-xs text-slate-500">{action.time}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="w-full mt-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all">View All Logs</button>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
